/* eslint-disable no-console */

/**
 * Tree Builder Module
 * Handles ASCII tree structure creation and manipulation
 */

export class TreeBuilder {
  constructor () {
    console.log('🌳 [TREE] TreeBuilder constructor called');
    this.tree = null;
    this.currentNode = null;
    this.operationCount = 0;
    this.nodeCount = 0;
    this.lastOperation = null;
    this.performanceMetrics = {
      nodeAdditions: 0,
      nodeDeletions: 0,
      asciiRenders: 0,
      totalOperationTime: 0
    };
  }

  /**
   * Initialize a new tree structure
   */
  init () {
    console.log('🌳 [TREE] TreeBuilder initializing...');
    const startTime = performance.now();

    this.tree = {
      id: 'root',
      name: 'Betriebsstruktur',
      children: []
    };

    this.currentNode = null;
    this.nodeCount = 0;
    this.operationCount = 0;
    this.lastOperation = 'init';

    const initTime = performance.now() - startTime;
    console.log('✅ [TREE] TreeBuilder initialized:', {
      rootId: this.tree.id,
      rootName: this.tree.name,
      initTime: `${Math.round(initTime)}ms`
    });
  }

  /**
   * Add a node to the tree
   */
  addNode (parentId, nodeData) {
    const startTime = performance.now();
    this.operationCount++;
    this.lastOperation = 'addNode';

    console.log('➕ [TREE] Adding node:', {
      parentId,
      nodeName: nodeData.name,
      nodeType: nodeData.type || 'area',
      hasMetadata: !!nodeData.metadata
    });

    const node = {
      id: this.generateId(),
      name: nodeData.name,
      type: nodeData.type || 'area',
      children: [],
      metadata: nodeData.metadata || {}
    };

    const parent = this.findNode(this.tree, parentId);
    if (parent) {
      parent.children.push(node);
      this.nodeCount++;
      this.performanceMetrics.nodeAdditions++;

      const addTime = performance.now() - startTime;
      this.performanceMetrics.totalOperationTime += addTime;

      console.log('✅ [TREE] Node added successfully:', {
        nodeId: node.id,
        nodeName: node.name,
        parentId: parent.id,
        treeDepth: this.getTreeDepth(),
        totalNodes: this.nodeCount,
        time: `${Math.round(addTime)}ms`
      });
    } else {
      console.error('❌ [TREE] Failed to add node - parent not found:', parentId);
      const addTime = performance.now() - startTime;
      console.log('⏱️ [TREE] Failed add operation time:', `${Math.round(addTime)}ms`);
    }

    return node;
  }

  /**
   * Get tree depth for logging purposes
   */
  getTreeDepth (node = this.tree, depth = 0) {
    if (!node) return 0;
    if (!node.children || node.children.length === 0) return depth;

    const childDepths = node.children.map(child => this.getTreeDepth(child, depth + 1));
    return Math.max(...childDepths);
  }

  /**
   * Remove a node from the tree
   */
  removeNode (nodeId) {
    return this.removeNodeRecursive(this.tree, nodeId);
  }

  /**
   * Convert tree to ASCII representation
   */
  toASCII () {
    const startTime = performance.now();
    this.performanceMetrics.asciiRenders++;

    console.log('🎨 [TREE] Generating ASCII representation:', {
      hasTree: !!this.tree,
      nodeCount: this.nodeCount,
      renderCount: this.performanceMetrics.asciiRenders
    });

    if (!this.tree) {
      console.warn('⚠️ [TREE] Cannot generate ASCII - no tree structure');
      return '';
    }

    const ascii = this.renderASCIINode(this.tree, '', true);
    const renderTime = performance.now() - startTime;

    console.log('✅ [TREE] ASCII generation completed:', {
      asciiLength: ascii.length,
      renderTime: `${Math.round(renderTime)}ms`,
      totalRenders: this.performanceMetrics.asciiRenders
    });

    return ascii;
  }

  /**
   * Find a node in the tree by ID
   */
  findNode (node, nodeId) {
    if (node.id === nodeId) return node;

    for (const child of node.children) {
      const found = this.findNode(child, nodeId);
      if (found) return found;
    }

    return null;
  }

  /**
   * Generate unique ID for nodes
   */
  generateId () {
    return `node_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Recursively remove a node
   */
  removeNodeRecursive (node, nodeId) {
    const index = node.children.findIndex(child => child.id === nodeId);
    if (index > -1) {
      const removed = node.children.splice(index, 1);
      return removed[0];
    }

    for (let i = 0; i < node.children.length; i++) {
      const removed = this.removeNodeRecursive(node.children[i], nodeId);
      if (removed) return removed;
    }

    return null;
  }

  /**
   * Render node as ASCII
   */
  renderASCIINode (node, prefix = '', isLast = true) {
    const branch = isLast ? '└── ' : '├── ';
    let result = `${prefix}${branch}${node.name}\n`;

    const children = node.children || [];
    children.forEach((child, index) => {
      const isChildLast = index === children.length - 1;
      const childPrefix = `${prefix}${isLast ? '    ' : '│   '}`;
      result += this.renderASCIINode(child, childPrefix, isChildLast);
    });

    return result;
  }

  /**
   * Export tree data
   */
  export () {
    return JSON.parse(JSON.stringify(this.tree));
  }

  /**
   * Import tree data
   */
  import (data) {
    this.tree = JSON.parse(JSON.stringify(data));
    this.nodeCount = Math.max(this.countNodes(this.tree) - 1, 0);
    this.currentNode = null;
  }

  countNodes (node) {
    if (!node) return 0;
    const children = node.children || [];
    return 1 + children.reduce((sum, child) => sum + this.countNodes(child), 0);
  }

  /**
   * Get tree statistics
   */
  getStats () {
    if (!this.tree) return { nodes: 0, depth: 0, types: {} };

    const stats = {
      nodes: 0,
      depth: 0,
      types: {}
    };

    this.calculateStats(this.tree, stats, 0);
    return stats;
  }

  /**
   * Calculate statistics recursively
   */
  calculateStats (node, stats, depth) {
    stats.nodes++;
    stats.depth = Math.max(stats.depth, depth);
    stats.types[node.type] = (stats.types[node.type] || 0) + 1;

    if (node.children) {
      for (const child of node.children) {
        this.calculateStats(child, stats, depth + 1);
      }
    }
  }
}
